import React, { useState } from 'react';
import { Inline, Box, Placeholder } from '@stitch-fix/mode-react';
import { InView } from 'react-intersection-observer';
import OutfitItemCard from '../OutfitItemCard';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';
import { shopPdpPath } from '../../utils/urlUtils';
import { ShopOutfitFragmentFragment } from '../ProductDetailsPage/ShopOutfitFragment';
import {
  trackOutfitContextCarouselView,
  trackOutfitContextItemClick,
} from './OutfitItemSelect.tracking';

type OutfitItemType = ShopOutfitFragmentFragment['items'][number];

const getWindowDimensions = () => {
  const { innerWidth: width } = window;

  return {
    width,
  };
};

const OverflowContainer: React.FC = ({ children }) => {
  const style = {
    maxWidth: getWindowDimensions().width,
    overflow: 'scroll',
  };

  return <Box style={style}>{children}</Box>;
};

const OutfitItemSelect = () => {
  const [state] = useProductDetailsPageContext();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const outfit: any = state.waysToWearIt?.items?.nodes[0] || null;

  const contextOutfit = {
    items: outfit?.items.map((item: OutfitItemType) => {
      return {
        ...item,
        shopProductVariant: {
          ...item.shopProductVariant,
          url: shopPdpPath(item.shopProductVariant?.url, {
            collection: state.collectionName,
            outfitId: state.outfitId,
            recommendedSkuId: item.shopProductVariant?.sku,
            source: state.source,
            subSource: 'shoppable-outfit',
          })
            .replace('product', state.urlScope)
            .replace('https://www.stitchfix.com', ''),
        },
      };
    }),
  };

  const initialOutfitItem = contextOutfit?.items?.filter(
    (item: OutfitItemType) =>
      item.shopProductVariant?.id ===
      (state.selectedVariant?.sku || state.fallbackVariant?.sku),
  )[0];

  const [outfitItemValue, setOutfitItemValue] = useState(initialOutfitItem);

  const onInView = (inView: boolean) => {
    if (inView) {
      trackOutfitContextCarouselView(state);
      setOutfitItemValue(initialOutfitItem);
    }
  };

  const onOutfitItemSelect = (value: OutfitItemType) => {
    trackOutfitContextItemClick(state, value.shopProductVariant?.sku);
    setOutfitItemValue(value);
  };

  const placeholderOutfitItemSelect = (
    <Inline data-testid="item-select-placeholder" spacing={0.5}>
      {state.outfitId?.split('-').map(() => (
        <Placeholder variant="rect" width={4} height={5.6} />
      ))}
    </Inline>
  );

  const loadedOutfitItemSelect =
    contextOutfit?.items?.length > 0 ? (
      <Box>
        <InView threshold={1} onChange={onInView} triggerOnce>
          <OverflowContainer>
            <Inline data-testid="item-select" spacing={0.5}>
              {contextOutfit?.items?.map((outfitItem: OutfitItemType) => (
                <OutfitItemCard
                  key={outfitItem.shopProductVariant?.id}
                  isSelected={
                    outfitItem.shopProductVariant?.id ===
                    outfitItemValue?.shopProductVariant?.id
                  }
                  onClick={onOutfitItemSelect}
                  outfitItem={outfitItem}
                />
              ))}
            </Inline>
          </OverflowContainer>
        </InView>
      </Box>
    ) : null;

  return state.waysToWearItLoading
    ? placeholderOutfitItemSelect
    : loadedOutfitItemSelect;
};

export default OutfitItemSelect;
